<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { getImageByPriority } from '@/views/search';
import { Constants } from '@/utilities/constants.ts';
import { getProjectCard, type GetProjectCardResponse } from '@/api';
import { useSsgState } from '@/utilities/ssg-state.ts';

const props = defineProps<{
  index: number;
  projectId: number;
}>();

const project = ref<GetProjectCardResponse | null>(null);
const loadError = ref<unknown | null>(null);

const loadProject = async () => {
  try {
    loadError.value = null;
    const projectResult = await getProjectCard({
      path: {
        projectId: props.projectId,
      },
    });

    if (!projectResult.response?.ok || !projectResult.data) {
      throw new Error(`Failed to fetch project. ${JSON.stringify(projectResult.error)}`);
    }

    project.value = projectResult.data;
  } catch (err) {
    loadError.value = err;
    console.error(err);
  }
};

useSsgState({
  key: `project-card:${props.projectId}`,
  refs: { project },
  isLoaded: () => project.value !== null,
  load: loadProject,
});

watch(
  () => props.projectId,
  () => {
    void loadProject();
  },
  { immediate: false }
);

const image = computed(() => (project.value ? getImageByPriority(project.value) : null));
const fetchPriority = computed(() => (props.index === 0 ? 'high' : 'auto'));
const loadingPriority = computed(() => (props.index < 2 ? 'eager' : 'lazy'));
const builderProjectSizes = `(max-width: ${Constants.Breakpoints.sm}) 60vw, ${Constants.ImageSizes.Small.Width}px`;
</script>

<template>
  <li v-if="project && image" class="xs:w-1/3 w-1/2 shrink-0 sm:w-1/4 md:w-1/5 lg:w-1/6">
    <div class="xs:p-1 group relative p-1">
      <div class="aspect-h-2 aspect-w-3 block w-full overflow-hidden rounded-lg">
        <img
          :alt="image.title"
          :fetchpriority="fetchPriority"
          :height="Constants.ImageSizes.Small.Height"
          :loading="loadingPriority"
          :sizes="builderProjectSizes"
          :src="image.src.resizedToSmall()"
          :srcset="image.src.toSrcset()"
          :width="Constants.ImageSizes.Small.Width"
          class="object-cover"
        />
      </div>
      <h3 class="mt-2 block truncate text-xs font-medium whitespace-nowrap text-gray-900">
        {{ project.name }}
      </h3>
      <p class="block truncate text-xs font-medium whitespace-nowrap text-gray-500">{{ project.address.city.name }}, {{ project.address.province }}</p>
      <RouterLink :to="{ name: 'project', params: { slug: project.name.toSlug(), projectId: project.id } }" class="focus-within:ring-ph-green-600 absolute inset-0 cursor-pointer rounded-lg focus-within:ring-2 focus-within:outline-hidden">
        <span class="sr-only">{{ project.name }}</span>
      </RouterLink>
    </div>
  </li>
</template>

<style lang="css" scoped></style>
